import React, {useState, useEffect} from "react"
import Select, {SelectOption} from "@irati/ui/src/FormItem/Select"
import {TeamAssignment, User as UserType} from "@irati/types"
import authentication from "@irati/auth"
import {FormatMessage} from "@irati/utils/lang/types"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import userService from "@irati/identity/services/users"
import {isGlobalAdmin} from "@irati/identity/helpers/user/role"

enum Roles {
  User = "user",
  Manager = "manager",
  Admin = "admin"
}

interface Props extends InjectIntl {
  user: UserType
  onChange?: () => Promise<any>
}

const EditUserRole: React.FC<Props> = ({formatMessage, user, onChange}) => {
  const [loading, setLoading] = useState(false)
  const [role, setRole] = useState(Roles.User)
  const [options, setOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    let currentRole = Roles.User
    if (
      user.teamAssignments?.find(
        ({teamId, assignment}) =>
          teamId === authentication.business().rootTeamId &&
          (assignment === TeamAssignment.Manager || assignment === TeamAssignment.Leader)
      )
    ) {
      currentRole = Roles.Manager
    }
    if (isGlobalAdmin(authentication.business(), user)) {
      currentRole = Roles.Admin
    }

    setRole(currentRole)
  }, [user])

  useEffect(() => {
    setOptions(makeOptions(formatMessage, role))
  }, [role, formatMessage])

  const handleChange = async (value: Roles) => {
    setLoading(true)
    setRole(value)

    await makeRoleChange(user.uuid!, value)
    if (onChange) {
      await onChange()
    }

    setLoading(false)
  }

  return (
    <Select
      label="admin.components.userProfilePopUp.form.fields.select.label"
      aria-label="Select User Role"
      value={role}
      options={options}
      isSearchable={false}
      isClearable={false}
      isLoading={loading}
      name="rolesSelect"
      onChange={handleChange}
    />
  )
}

export default injectIntl(EditUserRole)

function makeOptions(formatMessage: FormatMessage, currentRole: Roles): SelectOption[] {
  const arr = [{value: Roles.User, label: formatMessage({id: "admin.roles.user"})}]
  if (currentRole === Roles.Manager) {
    // only include legacy manager option role if they are currently a legacy manager. Once they change this option will disappear.
    arr.push({value: Roles.Manager, label: formatMessage({id: "admin.roles.manager"})})
  }
  if (authentication.isGlobalAdmin() || authentication.isUserGhost()) {
    arr.push({value: Roles.Admin, label: formatMessage({id: "admin.roles.admin"})})
  }

  return arr
}

function makeRoleChange(userId: string, role: Roles) {
  if (role === Roles.User) {
    return userService.makeUserBasic(authentication.business().rootTeamId, userId)
  }
  return userService.makeUserGlobalAdmin(userId)
}
